import {
  AxiosError,
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { overviewMockHandlers } from "./handlers/overview.mock";
import type {
  ApiMockHandler,
  ApiMockRequestContext,
  ApiMockResponse,
  ApiMockService,
} from "./types";

const builtInMockHandlers: ApiMockHandler[] = [...overviewMockHandlers];
const registeredMockHandlers: ApiMockHandler[] = [];
let mockEnabledOverride: boolean | undefined;

function createContext(
  service: ApiMockService,
  config: InternalAxiosRequestConfig,
): ApiMockRequestContext {
  return {
    service,
    method: (config.method ?? "get").toLowerCase(),
    url: (config.url ?? "").split("?")[0],
    params: config.params,
    data: config.data,
  };
}

function createFetchContext(
  service: ApiMockService,
  input: RequestInfo | URL,
  init: RequestInit,
): ApiMockRequestContext {
  const request =
    typeof Request !== "undefined" && input instanceof Request
      ? input
      : undefined;
  const rawUrl = request?.url ?? input.toString();
  let url = rawUrl.split("?")[0];

  try {
    url = new URL(rawUrl, window.location.origin).pathname;
  } catch {
    // 相对 URL 或非浏览器环境下保留调用方提供的路径。
  }

  let data: unknown = init.body;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data) as unknown;
    } catch {
      // 非 JSON body（例如流式文本）保持原值，交由 handler 自行判断。
    }
  }

  return {
    service,
    method: (init.method ?? request?.method ?? "get").toLowerCase(),
    url,
    params: undefined,
    data,
  };
}

function matchesUrl(pattern: ApiMockHandler["url"], url: string): boolean {
  if (typeof pattern === "string") {
    return pattern === url;
  }

  pattern.lastIndex = 0;
  return pattern.test(url);
}

function findMockHandler(
  context: ApiMockRequestContext,
): ApiMockHandler | undefined {
  return [...registeredMockHandlers, ...builtInMockHandlers].find(
    (handler) =>
      handler.service === context.service &&
      handler.method.toLowerCase() === context.method &&
      matchesUrl(handler.url, context.url) &&
      (handler.match?.(context) ?? true),
  );
}

function wait(delayMs: number | undefined): Promise<void> {
  if (!delayMs || delayMs <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

function createResponse(
  config: InternalAxiosRequestConfig,
  mock: ApiMockResponse,
): AxiosResponse {
  return {
    data: mock.data,
    status: mock.status ?? 200,
    statusText: mock.statusText ?? "OK",
    headers: mock.headers ?? {},
    config,
    request: { mock: true },
  };
}

function isSuccessful(response: AxiosResponse): boolean {
  const validateStatus = response.config.validateStatus;
  return !validateStatus || validateStatus(response.status);
}

/** VITE_ENABLE_API_MOCK=true 可设置默认值；运行期开关优先级更高。 */
export function isApiMockEnabled(): boolean {
  return mockEnabledOverride ?? import.meta.env.VITE_ENABLE_API_MOCK === "true";
}

/** 供开发工具或 UI 接手环境在当前浏览器会话中显式切换 Mock。 */
export function setApiMockEnabled(enabled: boolean): void {
  mockEnabledOverride = enabled;
}

/** 恢复到 VITE_ENABLE_API_MOCK 指定的默认值。 */
export function resetApiMockEnabled(): void {
  mockEnabledOverride = undefined;
}

/**
 * 注册临时 Mock；后注册的处理器优先级更高。
 * 返回的清理函数适合组件卸载或测试结束时调用。
 */
export function registerApiMock(handler: ApiMockHandler): () => void {
  registeredMockHandlers.unshift(handler);

  return () => {
    const index = registeredMockHandlers.indexOf(handler);
    if (index >= 0) {
      registeredMockHandlers.splice(index, 1);
    }
  };
}

/** 仅供测试与开发工具重置运行时注册的 Mock。 */
export function clearRegisteredApiMocks(): void {
  registeredMockHandlers.length = 0;
}

/**
 * 命中 Mock 时为 Axios 请求注入 adapter，未命中时返回 undefined，调用方保持真实网络请求。
 */
export function getApiMockAdapter(
  service: ApiMockService,
  config: InternalAxiosRequestConfig,
): AxiosAdapter | undefined {
  if (!isApiMockEnabled()) {
    return undefined;
  }

  const handler = findMockHandler(createContext(service, config));
  if (!handler) {
    return undefined;
  }

  return async (adapterConfig) => {
    const mock = await handler.reply(createContext(service, adapterConfig));
    await wait(mock.delayMs);
    const response = createResponse(adapterConfig, mock);

    if (isSuccessful(response)) {
      return response;
    }

    throw new AxiosError(
      `Mock request failed with status code ${response.status}`,
      AxiosError.ERR_BAD_RESPONSE,
      adapterConfig,
      response.request,
      response,
    );
  };
}

/**
 * 原生 fetch 的等价入口。流式 API 需显式使用此函数，才能与 Axios 请求共享同一开关与 registry。
 */
export async function fetchWithApiMock(
  service: ApiMockService,
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  if (!isApiMockEnabled()) {
    return fetch(input, init);
  }

  const handler = findMockHandler(createFetchContext(service, input, init));
  if (!handler) {
    return fetch(input, init);
  }

  const mock = await handler.reply(createFetchContext(service, input, init));
  await wait(mock.delayMs);
  const headers = new Headers(mock.headers);
  if (typeof mock.data !== "string" && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const status = mock.status ?? 200;
  const body =
    status === 204
      ? null
      : typeof mock.data === "string"
        ? mock.data
        : JSON.stringify(mock.data);
  return new Response(body, {
    status,
    statusText: mock.statusText,
    headers,
  });
}

export type {
  ApiMockHandler,
  ApiMockRequestContext,
  ApiMockResponse,
  ApiMockService,
} from "./types";
